<script lang="ts">
	import { t } from '$i18n/i18n'
	import ArrowLeft from '$svg/icon-arrow-left.svg?component'
	import { LoadingAnimation } from '$types/enums'
	import { setContext } from 'svelte'
	import { modals } from 'svelte-modals'
	import Modal from '../organisms/Modal.svelte'
	import { hideAnimation, showAnimation } from '../stores'
	import MailConfigModal from '$lib/mail/MailConfigModal.svelte'
	import MmLoadingAnimation from '$lib/animations/MmLoadingAnimation.svelte'
	import Basic from '$lib/templates/Basic.svelte'
	import {
		fetchStateOrdinal,
		fetchShouldSendLetsGoMail,
		sendMailRequest,
		fetchAndApplyAccountingMailData,
		applyMailDurationChange,
		triggerMailDispatching,
		applyMailingListUpdate
	} from './LotAccountingMailConfigurationPage/LotAccountingMailConfiguration.controller'
	import { accountingMailConfigState } from './LotAccountingMailConfigurationPage/accountingMailConfigState.svelte'
	import { SvelteDate } from 'svelte/reactivity'
	import Button from '$lib/atoms/Button.svelte'
	import AccountingMailTimeline from './LotAccountingMailConfigurationPage/AccountingMailTimeline.svelte'
	import LetsGoMailSwitch from './LotAccountingMailConfigurationPage/LetsGoMailSwitch.svelte'

	let { lotId }: { lotId: string } = $props()

	// the singleton survives navigation — reset before the $effect below registers, so a
	// stale mailingList/mailingListSize mismatch from the previous lot cannot fire a
	// cross-lot updateMailingList POST
	accountingMailConfigState.reset()

	// --- State ---
	let disabledLetsGoMail = $state(false)
	let shouldSendLetsGoMail = $state(false)
	let stateOrdinal = $state(0)
	let showTimeline = $state(false)

	setContext('useMaterialDesign', true)

	const today = $derived.by(() => {
		let date = new SvelteDate()
		date.setHours(23)
		date.setMinutes(0)
		date.setSeconds(0)
		return date
	})

	// --- Load ---
	async function loadData() {
		stateOrdinal = await fetchStateOrdinal(lotId)
		await handleLetsGoMails()
		if (stateOrdinal >= 7) await handleAccountingMails() // HANDLING
	}

	async function handleLetsGoMails() {
		shouldSendLetsGoMail = await fetchShouldSendLetsGoMail(lotId)
		if (stateOrdinal >= 5) {
			// BIDDING
			disabledLetsGoMail = true
		}
	}

	async function handleAccountingMails() {
		// Await hydration before showing timeline. Singleton `accountingMailConfigState`
		// persists across navigations / stories, so rendering before `fromData()` lets the
		// previous mount's values flash through.
		await fetchAndApplyAccountingMailData(lotId, accountingMailConfigState)
		disabledLetsGoMail = true
		showTimeline = true
	}

	// --- Mail duration ---
	// --- Mailing list ---
	// $effect is necessary here: ChipList (Svelte 4) dispatches 'chipRemoved' for
	// deletions but has no 'chipAdded' event for additions. The effect detects both
	// by comparing list length to the last-synced size. mailingListSize is updated
	// after each successful save to prevent re-triggering.
	$effect(() => {
		const currentLength = accountingMailConfigState.mailingList?.length ?? 0
		if (
			accountingMailConfigState.mailingList !== null &&
			!accountingMailConfigState.readOnly &&
			currentLength !== accountingMailConfigState.mailingListSize
		) {
			void applyMailingListUpdate(lotId, accountingMailConfigState)
		}
	})

	// --- Trigger dispatching ---
	function handleTriggerMailDispatching() {
		if (accountingMailConfigState.isMailDispatchingDisabled) {
			modals.open(Modal, {
				title: $t('UI.mail.config.modal.title.start.dispatching'),
				onAccept: () => {
					void triggerMailDispatching(lotId, accountingMailConfigState)
					modals.close()
				},
				message: $t('UI.mail.config.modal.message.start.dispatching'),
				acceptText: $t('UI.mail.config.modal.button.start.dispatching')
			})
		} else {
			modals.open(Modal, {
				title: $t('UI.mail.config.modal.title.break.dispatching'),
				onDeny: () => {
					void triggerMailDispatching(lotId, accountingMailConfigState)
					modals.close()
				},
				message: $t('UI.mail.config.modal.message.stop.dispatching'),
				denyText: $t('UI.mail.config.modal.button.break.dispatching')
			})
		}
	}

	// --- Send mail ---
	function handleSendMail() {
		const indexOfMail =
			accountingMailConfigState.accountingMailConfigData.accountingMailData.filter(
				(d) => d.dispatchDateMail
			).length
		const mailTitle =
			accountingMailConfigState.accountingMailConfigData.accountingMailData[indexOfMail].subject
		modals.open(Modal, {
			title: $t('UI.mail.config.modal.title.sentMail', { mail: mailTitle.replace(' : ', ':') }),
			message: $t('UI.mail.config.modal.message.sentMail'),
			onAccept: () => {
				handleSendMailRequest(indexOfMail, mailTitle)
				modals.close()
			},
			acceptText: $t('UI.mail.config.modal.button.sendMail.title')
		})
	}

	async function handleSendMailRequest(mailIndex: number, mailTitle: string) {
		showAnimation(LoadingAnimation.MmLoader)
		await sendMailRequest(lotId, mailIndex, mailTitle)
		// Changes in the elastic DB need ~1s to propagate
		setTimeout(() => void fetchAndApplyAccountingMailData(lotId, accountingMailConfigState), 1100)
		hideAnimation()
	}

	function showSendMail(index: number, mailingListStr: string) {
		const content =
			accountingMailConfigState.accountingMailConfigData.accountingMailData[index].htmlContent
		const header =
			"<p style='font-weight: 600;margin: auto;padding-bottom: 0.3rem'>Versendet an:</p>"
		const ttContent =
			header +
			mailingListStr.toString().replaceAll(',', '<br>').replaceAll('>', '>• ').replace('<br>', '')
		modals.open(MailConfigModal, {
			content,
			title: $t('UI.mail.config.modal.mailingList'),
			ttContent
		})
		setTimeout(() => {
			document.querySelector('.mcnImage')?.setAttribute('src', '/assets/images/mail_logo_new.png')
		}, 100)
	}

	const redirectToLotDetailsPage = () => {
		window.parent.location.assign(`/lot/${lotId}/basedata`)
	}
</script>

<Basic>
	<div>
		<div class="lot-mail-config-container">
			<span>{$t('UI.mail.config.headline')}</span>
			{#await loadData()}
				<MmLoadingAnimation />
			{:then}
				{#if showTimeline}
					<AccountingMailTimeline
						{today}
						onMailDurationChange={(mailType, newDuration) =>
							applyMailDurationChange(lotId, mailType, newDuration, accountingMailConfigState)}
						onTriggerMailDispatching={handleTriggerMailDispatching}
						onSendMail={handleSendMail}
						onShowSentMail={showSendMail}
					/>
				{/if}
				<LetsGoMailSwitch bind:shouldSendLetsGoMail disabled={disabledLetsGoMail} {lotId} />
				<div class="button">
					<Button variant="secondary" onclick={redirectToLotDetailsPage}>
						<ArrowLeft width="20px" height="20px" />
						{$t('UI.mail.config.button.backToLot')}
					</Button>
				</div>
			{/await}
		</div>
	</div>
</Basic>

<style lang="scss">
	@import '$styles/global';

	.button {
		grid-area: buttons;
		margin-top: 3rem;
		display: flex;
		align-items: flex-start;
		width: 100%;
	}

	.lot-mail-config-container {
		@include flex-col($justify: flex-start);

		width: 100%;

		&:first-child {
			float: left;
			margin-top: 3.785rem;
		}

		& span {
			align-self: flex-start;

			@include roboto-font(normal, 500, 1.25rem, black);
		}
	}
</style>
